/**
 * Intervistat — regjistrime të vërteta, jo listë kandidatësh e filtruar.
 *
 * Deri tani kjo faqe ishte lista e aplikimeve me `status='interview'`. Nuk
 * kishte datë, orë, intervistues, vend, as rezultat: «Intervistë» ishte fjalë
 * te kolona e statusit. Një kandidat mund të kishte vetëm një, dhe askush s'e
 * dinte kur ishte apo si shkoi.
 *
 * Tani lexon `/api/interviews`. Pamjet (sot, kjo javë, të ardhshme, të kaluara)
 * llogariten te serveri — përndryshe do të duhej të lexoheshin të gjitha për
 * të treguar tre.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, Typography, Stack, Chip, Button, IconButton, Tooltip, Avatar,
  TextField, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, CircularProgress, Rating, Divider, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import RateReviewRoundedIcon from '@mui/icons-material/RateReviewRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import VideocamRoundedIcon from '@mui/icons-material/VideocamRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import PageHeader from '../components/common/PageHeader';
import usePermissions from '../hooks/usePermissions';
import { authFetch } from '../services/apiServices';
import {
  INTERVIEW_STATUSES, INTERVIEW_RECOMMENDATIONS,
  interviewKindLabel, interviewStatusMeta, recommendationLabel,
} from '../config/recruitmentPipeline';

const C = { ink: '#0F172A', ink500: '#64748B', line: '#E5E7EB', surface: '#F8FAFC', accent: '#3E8FA3' };

const VIEWS = [
  { key: 'today',    label: 'Sot' },
  { key: 'week',     label: 'Kjo javë' },
  { key: 'upcoming', label: 'Të ardhshme' },
  { key: 'past',     label: 'Të kaluara' },
  { key: 'all',      label: 'Të gjitha' },
];

const KIND_ICON = { online: VideocamRoundedIcon, phone: PhoneRoundedIcon, onsite: BusinessRoundedIcon };

const fmtWhen = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()} · ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

export default function InterviewsPage() {
  const { can } = usePermissions();
  const canManage = can('recruitment.create') || can('recruitment.edit') || can('recruitment.manage');

  const [view, setView] = useState('upcoming');
  const [status, setStatus] = useState('all');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);   // intervista që po vlerësohet
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const p = new URLSearchParams();
      if (view !== 'all') p.set('view', view);
      if (status !== 'all') p.set('status', status);
      const r = await authFetch(`/api/interviews?${p.toString()}`);
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j?.success === false) throw new Error(j?.error || `HTTP ${r.status}`);
      setRows(j.data || []);
    } catch (e) {
      setError(e?.message || 'Intervistat nuk u lexuan.');
    } finally { setLoading(false); }
  }, [view, status]);

  useEffect(() => { load(); }, [load]);

  const saveResult = async () => {
    setSaving(true);
    try {
      const r = await authFetch(`/api/interviews/${result.id}/result`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating: result.rating, recommendation: result.recommendation, comment: result.resultComment }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j?.success === false) throw new Error(j?.error || `HTTP ${r.status}`);
      setResult(null);
      await load();
    } catch (e) { setError(e?.message || 'Rezultati nuk u ruajt.'); }
    finally { setSaving(false); }
  };

  const counts = useMemo(() => {
    const c = {};
    rows.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [rows]);

  return (
    <Box>
      <PageHeader title="Intervistat" subtitle="Intervistat e planifikuara, me intervistuesit dhe rezultatin e tyre." />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
        <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v) => v && setView(v)}>
          {VIEWS.map((v) => (
            <ToggleButton key={v.key} value={v.key} sx={{ textTransform: 'none', px: 1.5 }}>{v.label}</ToggleButton>
          ))}
        </ToggleButtonGroup>
        <TextField select size="small" label="Statusi" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 170 }}>
          <MenuItem value="all">Të gjitha</MenuItem>
          {INTERVIEW_STATUSES.map((s) => <MenuItem key={s.code} value={s.code}>{s.label}</MenuItem>)}
        </TextField>
        <Box sx={{ flex: 1 }} />
        {Object.entries(counts).map(([k, v]) => (
          <Chip key={k} size="small" label={`${interviewStatusMeta(k).label}: ${v}`}
            color={interviewStatusMeta(k).color} variant="outlined" />
        ))}
      </Stack>

      <Card elevation={0} sx={{ border: `1px solid ${C.line}`, borderRadius: 2 }}>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress size={24} /></Stack>
          ) : rows.length === 0 ? (
            <Stack alignItems="center" spacing={1} sx={{ py: 6, px: 2, textAlign: 'center' }}>
              <EventAvailableRoundedIcon sx={{ fontSize: 34, color: '#94A3B8' }} />
              <Typography sx={{ fontWeight: 700, color: C.ink }}>Nuk ka intervista të planifikuara.</Typography>
              <Typography variant="body2" color="text.secondary">
                {/* CTA-ja jeton te kandidati: intervista planifikohet për dikë,
                    jo më vete, ndaj një buton këtu do të kërkonte prapë kandidatin. */}
                Intervistat planifikohen nga dosja e kandidatit, te «Kandidatët».
              </Typography>
            </Stack>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: C.surface }}>
                    <TableCell sx={{ fontWeight: 700 }}>Kandidati</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Pozita</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Data dhe ora</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Lloji</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Intervistuesit</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Statusi</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Rezultati</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Veprime</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r) => {
                    const Icon = KIND_ICON[r.kind] || BusinessRoundedIcon;
                    const st = interviewStatusMeta(r.status);
                    return (
                      <TableRow key={r.id} hover>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: '#E7F4F7', color: C.accent }}>
                              {(r.candidateName || '?').slice(0, 1)}
                            </Avatar>
                            <Box>
                              <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{r.candidateName || '—'}</Typography>
                              <Typography sx={{ fontSize: 11.5, color: C.ink500 }}>Intervista #{r.sequence}</Typography>
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ fontSize: 13 }}>{r.position || '—'}</TableCell>
                        <TableCell sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>{fmtWhen(r.scheduledAt)}</TableCell>
                        <TableCell sx={{ fontSize: 13 }}>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Icon sx={{ fontSize: 15, color: C.ink500 }} />
                            <span>{interviewKindLabel(r.kind)}</span>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ fontSize: 12.5 }}>
                          {r.participants.length
                            ? r.participants.map((p) => p.name).join(', ')
                            : <Box component="span" sx={{ color: '#94A3B8' }}>—</Box>}
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={st.label} color={st.color} variant="outlined" sx={{ height: 22, fontWeight: 700 }} />
                        </TableCell>
                        <TableCell sx={{ fontSize: 12.5 }}>
                          {r.rating || r.recommendation
                            ? `${r.rating ? `${r.rating}/5` : ''}${r.rating && r.recommendation ? ' · ' : ''}${r.recommendation ? recommendationLabel(r.recommendation) : ''}`
                            : <Box component="span" sx={{ color: '#94A3B8' }}>—</Box>}
                        </TableCell>
                        <TableCell align="right">
                          {canManage && (
                            <Tooltip title="Shëno rezultatin" arrow>
                              <IconButton size="small" onClick={() => setResult({ ...r })}>
                                <RateReviewRoundedIcon sx={{ fontSize: 17 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* ── Rezultati i intervistës ── */}
      <Dialog open={Boolean(result)} onClose={() => setResult(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Rezultati i intervistës</DialogTitle>
        <DialogContent>
          {result && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {result.candidateName} · Intervista #{result.sequence} · {fmtWhen(result.scheduledAt)}
              </Typography>
              <Divider />
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.5 }}>Vlerësimi i përgjithshëm</Typography>
                <Rating value={result.rating || 0} onChange={(_, v) => setResult((p) => ({ ...p, rating: v }))} />
              </Box>
              <TextField select label="Rekomandimi" value={result.recommendation || ''} fullWidth size="small"
                onChange={(e) => setResult((p) => ({ ...p, recommendation: e.target.value }))}>
                <MenuItem value="">—</MenuItem>
                {INTERVIEW_RECOMMENDATIONS.map((r) => <MenuItem key={r.code} value={r.code}>{r.label}</MenuItem>)}
              </TextField>
              <TextField label="Koment" multiline minRows={3} fullWidth size="small"
                value={result.resultComment || ''}
                onChange={(e) => setResult((p) => ({ ...p, resultComment: e.target.value }))} />
              {/* Thuhet hapur: rekomandimi nuk vepron vetë. */}
              <Alert severity="info" sx={{ py: 0.25, fontSize: '.8rem' }}>
                Rekomandimi ruhet si këshillë. Faza e kandidatit ndryshohet veçmas, te dosja e tij.
              </Alert>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setResult(null)} color="inherit">Anulo</Button>
          <Button variant="contained" onClick={saveResult} disabled={saving}>
            {saving ? 'Duke ruajtur…' : 'Ruaj rezultatin'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
